import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { PageTypeEnumValidationSchema } from './enum';

export const ADD_TO_WEBSITE_MODES = ['inline', 'popup', 'link'] as const;

export const AddToWebsiteModeValidationSchema = z.enum(ADD_TO_WEBSITE_MODES).meta({
  description:
    'How the page shows on the website. `inline` renders it inside the page where the code is placed. `popup` opens it in an overlay when a customer clicks a button or link. `link` sends the customer to the hosted page. Omit to get all three and pick one.',
  example: 'inline',
});

export const AddToWebsiteInputValidationSchema = z.object({
  pageId: ObjectIdStringValidationSchema.meta({
    description:
      'The ID of the checkout page, form, or event. Get it from create_checkout_page, create_form, create_event, or the list_* and get_* tools.',
  }),
  pageType: PageTypeEnumValidationSchema.meta({
    description:
      'The kind of page: `checkout` for checkout pages, `form` for forms, `event` for events. It must match the page, or the page is not found.',
    example: 'checkout',
  }),
  mode: AddToWebsiteModeValidationSchema.optional(),
});

export const AddToWebsitePageResponseValidationSchema = z.object({
  data: z.object({
    id: ObjectIdStringValidationSchema,
    type: PageTypeEnumValidationSchema,
    name: z.string().nullish(),
    status: z.enum(['published', 'draft', 'archived']),
    url: z.url(),
  }),
});

const PlacementDescription =
  'Where and how to place the code on the website, written for the AI building the site.';

const ReactCodeDescription =
  'A self-contained React component (works in .jsx and .tsx files, including Next.js with "use client"). It loads the embed script once and renders the page. Use it instead of the HTML snippet when the site is built with React.';

export const AddToWebsiteToolDataValidationSchema = z
  .object({
    pageId: ObjectIdStringValidationSchema,
    pageType: PageTypeEnumValidationSchema,
    name: z.string().nullish().meta({ description: 'Internal name of the page.' }),
    status: z.enum(['published', 'draft', 'archived']).meta({
      description:
        'Publication status of the page. Customers can only use published pages; see `notes` when it is not published.',
    }),
    hostedUrl: z.url().meta({
      description: 'The live URL of the hosted page. The embed code loads this page.',
    }),
    scriptUrl: z.url().meta({
      description:
        'The Checkout Page embed script. Load it once per page, even when the page has several embeds or buttons.',
    }),
    inline: z
      .object({
        html: z.string().meta({
          description:
            'HTML for a responsive inline embed: a container div plus the embed script. The embed resizes to fit its content.',
        }),
        react: z.string().meta({ description: ReactCodeDescription }),
        placement: z.string().meta({ description: PlacementDescription }),
      })
      .optional()
      .meta({ description: 'Inline embed code. Present unless `mode` asked for another option.' }),
    popup: z
      .object({
        scriptHtml: z.string().meta({
          description: 'The embed script tag. Add it once, ideally in the <head> of the site.',
        }),
        buttonHtml: z.string().meta({
          description:
            'A link with the `cp-button` class that opens the page in an overlay. Any <a> or <button> with the class `cp-button` and this href works, so style it to match the site.',
        }),
        react: z.string().meta({ description: ReactCodeDescription }),
        placement: z.string().meta({ description: PlacementDescription }),
      })
      .optional()
      .meta({ description: 'Popup code. Present unless `mode` asked for another option.' }),
    link: z
      .object({
        html: z.string().meta({
          description: 'A plain link to the hosted page. Needs no script.',
        }),
        placement: z.string().meta({ description: PlacementDescription }),
      })
      .optional()
      .meta({ description: 'Plain link code. Present unless `mode` asked for another option.' }),
    notes: z.array(z.string()).meta({
      description:
        'Things to tell the user or fix before going live, such as an unpublished page. Empty when there is nothing to flag.',
    }),
  })
  .meta({ description: 'Ready-to-paste code that adds a Checkout Page page to a website.' });

export const AddToWebsiteToolResponseValidationSchema = z.object({
  data: AddToWebsiteToolDataValidationSchema,
});

export type AddToWebsiteInput = z.output<typeof AddToWebsiteInputValidationSchema>;
export type AddToWebsiteMode = z.output<typeof AddToWebsiteModeValidationSchema>;
export type AddToWebsiteToolData = z.output<typeof AddToWebsiteToolDataValidationSchema>;
